// @ts-nocheck
import { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Heading,
  Flex,
  Divider,
} from "@chakra-ui/react";
import { Drawer as DesignSystemDrawer } from "barte-design-system";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatCurrency, showToast } from "@/utils";
import { maskDocumentForPrivacy } from "@/utils/formatDocument";
import { FiDownload } from "react-icons/fi";
import { CheckCircleIcon, TimeIcon, WarningTwoIcon, RepeatIcon } from "@chakra-ui/icons";
import { PixListItem } from "@/services/Api/pixN8nService";
import { getBankNameByIspb } from "./receipt-boleto/format";
import CancelPaymentDrawer from "./CancelPixDrawer";
import CancelBankSlipDrawer from "./CancelBankSlipDrawer";
import { getPaymentStatusLabel, getPaymentStatusColors } from "@/services/Api/pixApiService";
import { Button } from "barte-design-system";
interface PaymentDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  payment: PixListItem | null;
}

const PaymentDetailDrawer = ({ isOpen, onClose, payment }: PaymentDetailDrawerProps) => {
  const [isCancelDrawerOpen, setIsCancelDrawerOpen] = useState(false);

  function toValidDate(input: any) {
    if (!input) return null;
    if (typeof input === "number") {
      const ms = input < 1e12 ? input * 1000 : input;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof input === "string") {
      const trimmed = input.trim();
      if (!trimmed) return null;
      if (/^\d+$/.test(trimmed)) {
        const n = Number(trimmed);
        const ms = n < 1e12 ? n * 1000 : n;
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const [y, m, d] = trimmed.split('-').map(Number);
        const dObj = new Date(Date.UTC(y, m - 1, d));
        return isNaN(dObj.getTime()) ? null : dObj;
      }
      const d = new Date(trimmed);
      return isNaN(d.getTime()) ? null : d;
    }
    if (input instanceof Date) {
      return isNaN(input.getTime()) ? null : input;
    }
    return null;
  }


  function getStatusIcon(status: string) {
    const s = String(status || '').toLowerCase();
    const { icon: color } = getPaymentStatusColors(status);
    switch (s) {
      case 'completed':
      case 'paid':
      case 'approved':
        return <CheckCircleIcon boxSize={2} color={color} />;
      case 'scheduled':
      case 'pending_approval':
      case 'pending':
        return <TimeIcon boxSize={2} color={color} />;
      case 'processing':
        return <RepeatIcon boxSize={2} color={color} />;
      case 'returned':
      case 'refunded':
        return <RepeatIcon boxSize={2} color={color} />;
      case 'rejected':
      case 'failed':
      case 'expired':
      case 'cancelled':
      case 'canceled':
        return <WarningTwoIcon boxSize={2} color={color} />;
      default:
        return <TimeIcon boxSize={2} color={color} />;
    }
  }

  const getStatusColor = (s: string) => getPaymentStatusColors(s).badgeBg;

  const getStatusLabel = (s: string) => getPaymentStatusLabel(s);

  const [isGenerating, setIsGenerating] = useState(false);

  const isPixTransaction = (transactionType?: string) =>
    transactionType === "PIX" || transactionType === "COPY_PASTE_PIX";

  const handleDownloadReceipt = async () => {
    if (!payment) return;
    try {
      setIsGenerating(true);
      if (isPixTransaction(payment.transactionType)) {
        if (!payment.transferId) {
          throw new Error("transferId ausente para comprovante Pix");
        }
        const { downloadPixReceipt } = await import("./receipt-pix/downloadPixReceipt");
        const { getPixPaymentReceipt } = await import("@/services/Api/pixN8nService");
        const receipt = await getPixPaymentReceipt(payment.transferId);
        await downloadPixReceipt(receipt);
      } else if (payment.transactionType === "BANK_SLIP") {
        const receiptId = payment.transferId || payment.transactionId;
        if (!receiptId) {
          throw new Error("identificador ausente para comprovante de boleto");
        }
        const { downloadBankSlipReceipt } = await import("./receipt-boleto/downloadBankSlipReceipt");
        const { getBankSlipPaymentReceipt } = await import("@/services/Api/bankSlipService");
        const receipt = await getBankSlipPaymentReceipt(receiptId);
        await downloadBankSlipReceipt(receipt);
      } else {
        throw new Error(`Tipo de pagamento sem comprovante disponível: ${payment.transactionType}`);
      }
      showToast({
        message: "Comprovante gerado com sucesso",
        type: "success",
      });
    } catch (error: any) {
      console.error("Erro ao gerar comprovante:", error);

      showToast({
        message: "Falha ao gerar comprovante",
        type: "error",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  if (!payment) return null;

  return (
    <>
      <DesignSystemDrawer
        title="Pagamento realizado"
        isOpen={isOpen}
        onClose={onClose}
        footer={
          String(payment.status || '').toUpperCase() === 'SCHEDULED'
            ? {
                secondaryButton: { children: "Fechar", variant: "outline", onClick: onClose },
                primaryButton: { children: "Cancelar pagamento", onClick: () => setIsCancelDrawerOpen(true) },
              }
            : { primaryButton: { children: "Fechar", variant: "outline", onClick: onClose } }
        }
      >
            <VStack spacing={6} align="stretch">
              <Box bg="#FAFAFA" p={6} w="full" borderBottom="1px solid #E0E0E0" borderTop="1px solid #E0E0E0">
                <HStack justify="space-between" align="start" w="full">
                  <Box>
                    <HStack align="baseline" spacing={2}>
                      <Text fontSize="2xl" color="gray.900" fontWeight="700" lineHeight="1">
                        {formatCurrency({ value: payment.amount || 0 }).replace('R$ ', '')}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      {(() => {
                        const date = toValidDate(`${payment.scheduledDate}T00:00:00`);
                        return date
                          ? format(date, "dd MMMM yyyy", { locale: ptBR })
                          : "—";
                      })()}
                    </Text>
                  </Box>

                  <Button
                    variant="outline"
                    iconLeft={<FiDownload />}
                    onClick={handleDownloadReceipt}
                    loading={isGenerating}
                  >
                    Baixar comprovante
                  </Button>
                </HStack>

                <HStack spacing={1} justify="space-between" align="center" w="full" mt={4}>
                  <Text fontSize="sm" color="gray.700" fontWeight="500">
                    Status
                  </Text>
                  <Badge
                    bg={getStatusColor(payment.status)}
                    color={getPaymentStatusColors(payment.status).text}
                    variant="solid"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="xs"
                    fontWeight="600"
                  >
                    <HStack spacing={1}>
                      {getStatusIcon(payment.status)}
                      <Text as="span" fontSize="14px" fontWeight="500">{getStatusLabel(payment.status)}</Text>
                      {String(payment.status || '').toLowerCase() === 'scheduled' && (
                        <Text as="span" fontSize="14px" fontWeight="500"> para {format(toValidDate(payment.scheduledDate) || new Date(), 'dd/MM/yy', { locale: ptBR })}</Text>
                      )}
                    </HStack>
                  </Badge>
                </HStack>

                <HStack justify="space-between" w="full" mt={1}>
                  <Text fontSize="sm" color="gray.700" fontWeight="500">
                    ID da transação
                  </Text>
                  <HStack spacing={1}>
                    <Text
                      fontSize="sm"
                      color="gray.900"
                      fontFamily="mono"
                      wordBreak="break-all"
                    >
                      {payment.transferId}
                    </Text>
                  </HStack>
                </HStack>
              </Box>
              <Box px={6}>
                <Heading size="md" color="gray.900" mb={1} fontWeight="600">
                  Quem recebeu
                </Heading>

                <VStack spacing={1} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">
                      Destinatário
                    </Text>
                    <Text fontSize="sm" color="gray.900" fontWeight="500">
                      {payment.recipientName || '—'}
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">
                      CNPJ/CPF
                    </Text>
                    <Text fontSize="sm" color="gray.900" fontFamily="mono">
                      {payment.recipientDocument || '—'}
                    </Text>
                  </HStack>

                  {<HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">
                      Instituição de destino
                    </Text>
                    <Text fontSize="sm" color="gray.900" fontWeight="500">
                      {getBankNameByIspb(payment.credit_bank_ispb) || '—'}
                    </Text>
                  </HStack>}

                  <HStack justify="space-between" align="start">
                    <Text fontSize="sm" color="gray.600">
                      Descrição
                    </Text>
                    <Text fontSize="sm" color="gray.900" fontWeight="500" textAlign="right" maxW="200px">
                      {(payment.description ?? '').trim() || '—'}
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              <Divider borderColor="gray.200" />

              <Box px={6}>
                <Heading size="md" color="gray.900" mb={1} fontWeight="600">
                  Quem enviou
                </Heading>

                <VStack spacing={1} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">
                      Remetente
                    </Text>
                    <Text fontSize="sm" color="gray.900" fontWeight="500">
                      {payment.payerName || payment.debit_name || '—'}
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">
                      CNPJ/CPF
                    </Text>
                    <Text fontSize="sm" color="gray.900" fontFamily="mono">
                      {(() => {
                        try {
                          const raw = localStorage.getItem('barte-user-info');
                          const info = raw ? JSON.parse(raw) : null;
                          return maskDocumentForPrivacy(info?.document);
                        } catch {
                          return '—';
                        }
                      })()}
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              <Divider borderColor="gray.200" />

              <Box px={6}>
                <VStack spacing={1} align="stretch" pt={2}>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">
                      Usuário
                    </Text>
                    <Text fontSize="sm" color="gray.900" fontWeight="500">
                      {payment.userEmail || payment.submittedBy || '—'}
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">
                      Centro de custo
                    </Text>
                    <Text fontSize="sm" color="gray.900" fontWeight="500" textTransform="capitalize">
                      {payment.costCenter || '—'}
                    </Text>
                  </HStack>

                  <HStack justify="space-between" align="start">
                    <Text fontSize="sm" color="gray.600">
                      Pix destino
                    </Text>
                    <Text
                      fontSize="sm"
                      color="gray.900"
                      fontFamily="mono"
                      textAlign="right"
                      wordBreak="break-all"
                      maxW="200px"
                    >
                      {payment.pixKey || payment.destinationAccount || '—'}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
      </DesignSystemDrawer>
      {payment && payment.transactionType === "PIX" && (
        <CancelPaymentDrawer
          isOpen={isCancelDrawerOpen}
          onClose={() => setIsCancelDrawerOpen(false)}
          payment={payment}
          onAfterCancel={() => {
            onClose();
          }}
        />
      )}
      {payment && payment.transactionType === "BANK_SLIP" && (
        <CancelBankSlipDrawer
          isOpen={isCancelDrawerOpen}
          onClose={() => setIsCancelDrawerOpen(false)}
          payment={payment}
          onAfterCancel={() => {
            onClose();
          }}
        />
      )}
    </>
  );
};

export default PaymentDetailDrawer;